import * as borrowerExporter from "./exporters/borrower";
import * as borrowerImporter from "./importers/borrower";
import { deep_clone_simple } from "../constants/utils";
import { default_borrower } from "../schema/borrower";
import { default_monthly_source } from "../schema/income_source";
import { default_loan } from "../schema/loan";
import * as propertyExporter from "./exporters/property";
import * as loanExporter from "./exporters/loan";
import * as assetExporter from "./exporters/asset";
import * as liabilityExporter from "./exporters/liability";
import * as feesExporter from "./exporters/fees";
import * as purchaseCreditsExporter from "./exporters/purchase_credits";
import * as relationshipsExporter from "./exporters/relationships";
import * as partyExporter from "./exporters/party";
import * as propertyImporter from "./importers/property";
import * as loanImporter from "./importers/loan";
import * as assetImporter from "./importers/asset";
import * as liabilityImporter from "./importers/liability";
import * as reoImporter from "./importers/reo";
import { default_reserve } from "../schema/reserve";
import { YES } from "../constants/yes_no";
import { mismoBool } from "./mismo_util";
import { attr, isBorrowerParty } from "./relationships";
import {
  payment_to_year,
  income_export_components
} from "../calculations/income";
import {
  net_income_or_loss,
  wrap_subject_property_if_needed
} from "../calculations/investment_reo";
import { getOwners } from "../constants/debt_owners";
import Decimal from "decimal.js";

export const ROOT_TAG_NAME = "MESSAGE";

// TODO correctly format this documentation
/**
 * The MISMO XML export config.
 *
 * This object represents the config of the root element in the resulting XLM document (with tag name `ROOT_TAG_NAME`).
 * There are special keys that can be added to any object in this config for customizing the output.
 * These keys always start with an "@" symbol, see below for more info.
 * All other keys in the objects represent children of the object that they are in, and must map to another object with config for that child.
 *
 * Customization options:
 *
 * "@att" - May map to an object containing keys and values corresponding to attributes' keys and values that should be added to the element.
 * For example, `FOO: { "@att": { foo: "bar" } }` becomes `<FOO foo="bar"></FOO>`.
 * You may also specify a function that takes in the context as the first and only argument and returns an object specifying attributes for the element.
 *
 * "@txt" - May map to a value that will become the text value of the element.
 * For example, `FOO: { "@txt": "bar" }` becomes `<FOO>bar<FOO>`.
 * You may also specify a function that takes in the context as the first and only argument and returns the text value for the element.
 * You should not specify children of the element if "@txt" is specified at all.
 *
 * "@populate" - May map to a function that takes in the builder for the current element as the first argument and the context as the second argument.
 * The function may add to or modify the element in any way necessary.
 * This is where most of the custom logic will be performed, meaning these functions probably shouldn't be defined inline as lambdas.
 *
 * "@skip" - May map to a function that takes in the context as the first and only argument and returns true if the element should not be created at all.
 *
 * "@forEach" - May map to a function that takes in the context as the first argument and must return an array of objects.
 * The element that this appears in will be repeated once for each object in the array.
 * Each object in the array is merged into the context for the corresponding element and its descendants.
 * So, do not return an array of borrowers directly (e.g. `[borrower1, borrower2]`), but rather return an array of objects with 1 key (e.g. `[{borrower: borrower1}, {borrower: borrwer2}]`).
 * All descendants will be able to access the corresponding object in the context, and this also makes it possible to have nested "@forEach" elements function correctly.
 */
export const EXPORT_CONFIG = {
  "@att": {
    "xmlns": "http://www.mismo.org/residential/2009/schemas",
    "xmlns:DU": "http://www.datamodelextension.org/Schema/DU",
    "xmlns:ULAD": "http://www.datamodelextension.org/Schema/ULAD",
    "xmlns:xlink": "http://www.w3.org/1999/xlink",
    "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
    "xsi:schemaLocation": "http://www.mismo.org/residential/2009/schemas DU_Wrapper_3.4.0_B324.xsd",
    "MISMOReferenceModelIdentifier": "3.4.032420160128"
  },
  ABOUT_VERSIONS: {
    ABOUT_VERSION: {
      AboutVersionIdentifier: { "@txt": "DU Spec 1.9.1" },
      CreatedDatetime: { "@txt": () => new Date().toISOString() }
    }
  },
  DEAL_SETS: {
    DEAL_SET: {
      DEALS: {
        DEAL: {
          ASSETS: {
            "@skip": ({ document }) => {
              const borrowers = (document.applicant && document.applicant.borrowers) || [];
              const hasFinancial = borrowers.some(
                borrower => (borrower.reserves || []).some(r => parseFloat(r.funds) > 0)
              );
              const hasReo = document.investment_properties && document.investment_properties.length > 0;
              const hasOwnedResidence = borrowers.some((b, i) =>
                assetExporter.residenceNeedsOwnedProperty(document, b, i)
              );
              return !hasFinancial && !hasReo && !hasOwnedResidence;
            },
            "@populate": assetExporter.populateDealAssets
          },
          COLLATERALS: {
            COLLATERAL: {
              SUBJECT_PROPERTY: {
                ADDRESS: { "@populate": propertyExporter.populatePropertyAddress },
                PROJECT: {
                  "@skip": propertyExporter.skipProject,
                  PROJECT_DETAIL: { "@populate": propertyExporter.populateProjectDetail }
                },
                PROPERTY_DETAIL: { "@populate": propertyExporter.populatePropertyDetail },
                PROPERTY_VALUATIONS: {
                  "@skip": propertyExporter.skipPropertyValuation,
                  PROPERTY_VALUATION: {
                    PROPERTY_VALUATION_DETAIL: {
                      "@populate": propertyExporter.populatePropertyValuation
                    }
                  }
                },
                SALES_CONTRACTS: {
                  "@skip": propertyExporter.skipSalesContracts,
                  SALES_CONTRACT: {
                    "@populate": propertyExporter.populateSalesContract
                  }
                }
              }
            }
          },
          LIABILITIES: {
            "@skip": ({ document }) => {
              return !Object.values(document.accountSummaries || {}).some(
                a => a.liability_type && parseFloat(a.monthly_payment) > 0
              );
            },
            "@populate": liabilityExporter.populateLiabilities
          },
          LOANS: {
            "@skip": ({ document }) => (
              !document.summary.selection_loan_number ||
              !document.loans[document.summary.selection_loan_number - 1]
            ),
            LOAN: {
              "@forEach": ({ document }) => [{ loan: document.loans[document.summary.selection_loan_number - 1] }],
              "@att": ({ loan }) => ({
                LoanRoleType: "SubjectLoan",
                "xlink:label": "LOAN_1"
              }),
              ADJUSTMENT: {
                "@skip": loanExporter.skipAdjustment,
                "@populate": loanExporter.populateAdjustment
              },
              AMORTIZATION: {
                "@skip": ({ loan }) => !loan.term,
                AMORTIZATION_RULE: { "@populate": loanExporter.populateAmortization }
              },
              CLOSING_INFORMATION: {
                "@skip": loanExporter.skipClosingInformation,
                CLOSING_INFORMATION_DETAIL: { "@populate": loanExporter.populateClosingInformation }
              },
              COMBINED_LTVS: {
                "@skip": loanExporter.skipCombinedLTVS,
                COMBINED_LTV: {
                  "@populate": loanExporter.populateCombinedLTV
                }
              },
              DOWN_PAYMENTS: {
                "@skip": loanExporter.skipDownPayments,
                DOWN_PAYMENT: {
                  "@populate": loanExporter.populateDownPayment
                }
              },
              // Closing Cost page -> itemized TRID fees for the LOS Loan
              // Estimate. DU ignores this block; an LOS reads it.
              FEE_INFORMATION: {
                "@skip": feesExporter.skipFeeInformation,
                "@populate": feesExporter.populateFeeInformation
              },
              HMDA_LOAN: {
                HMDA_LOAN_DETAIL: { "@populate": loanExporter.populateHMDALoan }
              },
              HOUSING_EXPENSES: {
                "@populate": loanExporter.populateHousingExpenses
              },
              LOAN_DETAIL: { "@populate": loanExporter.populateLoanDetail },
              MATURITY: {
                MATURITY_RULE: { "@populate": loanExporter.populateMaturity }
              },
              ORIGINATION_SYSTEMS: {
                ORIGINATION_SYSTEM: { "@populate": loanExporter.populateOriginationSystems }
              },
              // Down Payment / Adjustments credits (EMD, seller/RE/lender
              // credits, equity credits) -> PURCHASE_CREDIT. Purchase only.
              PURCHASE_CREDITS: {
                "@skip": purchaseCreditsExporter.skipPurchaseCredits,
                "@populate": purchaseCreditsExporter.populatePurchaseCredits
              },
              QUALIFYING_RATIOS: {
                QUALIFYING_RATIO: {
                  "@populate": loanExporter.populateQualifyingRatios
                }
              },
              // Refinance purpose (cash-out vs rate-and-term). Refinance only.
              REFINANCE: {
                "@skip": loanExporter.skipRefinance,
                "@populate": loanExporter.populateRefinance
              },
              TERMS_OF_LOAN: { "@populate": loanExporter.populateTermsOfLoan }
            }
          },
          PARTIES: {
            // Loan Originator + Loan Origination Company parties, from the LO's
            // Admin profile (threaded in via the export context). POINT reads
            // its Originator from the LoanOriginator party; we exported none.
            "@populate": partyExporter.populateOriginatorParties,
            PARTY: {
              "@forEach": ({ document }) => document.applicant.borrowers.map((borrower, borrowerIndex) => ({ borrower, borrowerIndex })),
              ADDRESSES: {
                ADDRESS: { "@populate": borrowerExporter.populateAddress }
              },
              INDIVIDUAL: {
                CONTACT_POINTS: { "@populate": borrowerExporter.populateContactPoints },
                IDENTIFICATION_VERIFICATION: { "@populate": borrowerExporter.populateIdentificationVerification },
                NAME: { "@populate": borrowerExporter.populateName }
              },
              ROLES: {
                ROLE: {
                  "@att": ({ borrowerIndex }) => ({
                    SequenceNumber: String((borrowerIndex + 1) * 10 + 1),
                    "xlink:label": `BORROWER_${(borrowerIndex + 1) * 10 + 1}`
                  }),
                  BORROWER: {
                    BORROWER_DETAIL: { "@populate": borrowerExporter.populateBorrowerDetail },
                    CREDIT_SCORES: {
                      // Emit one CREDIT_SCORE per bureau score we hold (with its
                      // CreditRepositorySourceType), so a credit pull round-trips
                      // all three. Falls back to a single range-derived score for
                      // manually-entered files. See exporters/borrower.
                      "@skip": ({ borrower }) => borrowerExporter.creditScoreExportItems(borrower).length === 0,
                      CREDIT_SCORE: {
                        "@forEach": ({ borrower }) => borrowerExporter.creditScoreExportItems(borrower),
                        CREDIT_SCORE_DETAIL: {
                          CreditRepositorySourceType: {
                            "@skip": ({ scoreItem }) => !scoreItem.bureau,
                            "@txt": ({ scoreItem }) => scoreItem.bureau
                          },
                          CreditScoreValue: {
                            "@txt": ({ scoreItem }) => scoreItem.value
                          }
                        }
                      }
                    },
                    CURRENT_INCOME: {
                      CURRENT_INCOME_ITEMS: {
                        CURRENT_INCOME_ITEM: {
                          // Each employment source expands into one item per
                          // income component (Base + non-zero Overtime / Bonus /
                          // Commissions / Tips). Sequence = source slot (10 apart)
                          // + the component offset; the RELATIONSHIPS arc uses the
                          // same formula so each item links to its employer.
                          "@forEach": ({ borrower, borrowerIndex, document }) => {
                            const items = [];
                            borrower.income.sources.forEach((incomeSource, sourceIndex) => {
                              income_export_components(incomeSource).forEach(comp => {
                                items.push({
                                  incomeSource,
                                  incomeType: comp.incomeType,
                                  otherDescription: comp.otherDescription,
                                  componentAnnual: comp.annual,
                                  seq: (borrowerIndex + 1) * 1000 + (sourceIndex + 1) * 10 + 101 + comp.offset
                                });
                              });
                            });
                            borrower.income.other_income.forEach((incomeOther, idx) => {
                              // Skip blank $0 other-income rows (a leftover empty
                              // entry the LO didn't delete) - nothing to export.
                              if (!(parseFloat(incomeOther.yearly_amount) > 0)) {
                                return;
                              }
                              items.push({
                                incomeOther,
                                seq: (borrowerIndex + 1) * 1000 + (borrower.income.sources.length + idx + 1) * 10 + 101
                              });
                            });
                            // Invest REO Schedule E net rental income (the "Net
                            // Income or Loss" total) is exported as a borrower-scoped
                            // 1e income item on the property's PRIMARY owner only, so
                            // POINT keeps it on the owning borrower (couple) and does
                            // not spread it across every applicant the way it does the
                            // deal-level OwnedPropertyRentalIncomeNetAmount. May be
                            // positive or negative. One item per owning borrower =
                            // the sum of the net for the properties they primarily own.
                            const reoNet = ((document && document.investment_properties) || [])
                              .reduce((sum, ip) => {
                                const owners = getOwners(ip);
                                const primaryOwner = owners.length > 0 ? parseInt(owners[0], 10) || 0 : 0;
                                if (primaryOwner !== borrowerIndex) return sum;
                                return sum.add(
                                  net_income_or_loss(
                                    wrap_subject_property_if_needed(document.property, ip)
                                  )
                                );
                              }, new Decimal(0));
                            if (!reoNet.isZero()) {
                              items.push({
                                reoNetRental: true,
                                componentAnnual: reoNet.mul(12),
                                seq: (borrowerIndex + 1) * 1000 + 991
                              });
                            }
                            return items;
                          },
                          "@att": ({ seq }) => ({
                            SequenceNumber: String(seq),
                            "xlink:label": `CURRENT_INCOME_ITEM_${seq}`
                          }),
                          CURRENT_INCOME_ITEM_DETAIL: { "@populate": borrowerExporter.populateIncomeItemDetail }
                        }
                      }
                    },
                    DECLARATION: {
                      DECLARATION_DETAIL: {
                        FHASecondaryResidenceIndicator: { "@txt": "false" },
                        HomeownerPastThreeYearsType: { "@txt": "Unknown" },
                        IntentToOccupyType: { "@txt": "Unknown" }
                      }
                    },
                    EMPLOYERS: {
                      // No employment sources -> skip the whole block instead of
                      // emitting an empty <EMPLOYERS/>.
                      "@skip": ({ borrower }) => !borrower.income.sources.length,
                      EMPLOYER: {
                        "@forEach": ({ borrower }) => borrower.income.sources.map((incomeSource, employerIndex) => ({ incomeSource, employerIndex })),
                        "@att": ({ borrowerIndex, employerIndex }) => ({
                          SequenceNumber: String((borrowerIndex + 1) * 100 + (employerIndex + 1) * 10 + 1),
                          "xlink:label": `EMPLOYER_${(borrowerIndex + 1) * 100 + (employerIndex + 1) * 10 + 1}`
                        }),
                        LEGAL_ENTITY: {
                          // No employer name and no phone -> don't emit an empty
                          // <LEGAL_ENTITY/> wrapper.
                          "@skip": ({ incomeSource }) =>
                            !incomeSource.employment_information.employer_name &&
                            !incomeSource.employment_information.phone,
                          CONTACTS: {
                            // typeof: an older import of an empty phone tag saved {}
                            "@skip": ({ incomeSource }) => typeof incomeSource.employment_information.phone !== "string"
                              || !incomeSource.employment_information.phone,
                            CONTACT: {
                              CONTACT_POINTS: {
                                CONTACT_POINT: {
                                  CONTACT_POINT_TELEPHONE: {
                                    ContactPointTelephoneValue: { "@txt": ({ incomeSource }) => incomeSource.employment_information.phone }
                                  }
                                }
                              }
                            }
                          },
                          LEGAL_ENTITY_DETAIL: {
                            "@skip": ({ incomeSource }) => !incomeSource.employment_information.employer_name,
                            FullName: { "@txt": ({ incomeSource }) => incomeSource.employment_information.employer_name }
                          }
                        },
                        ADDRESS: {
                          // No employer address entered -> skip the empty <ADDRESS/>.
                          "@skip": ({ incomeSource }) => {
                            const e = incomeSource.employment_information;
                            return !e.address && !e.city && !e.state && !e.zip;
                          },
                          "@populate": borrowerExporter.populateEmployerAddress
                        },
                        EMPLOYMENT: { "@populate": borrowerExporter.populateEmployerEmployment }
                      }
                    },
                    GOVERNMENT_MONITORING: {
                      "@populate": borrowerExporter.populateGovernmentMonitoring
                    },
                    RESIDENCES: {
                      RESIDENCE: {
                        ADDRESS: { "@populate": borrowerExporter.populateResidenceAddress },
                        RESIDENCE_DETAIL: { "@populate": borrowerExporter.populateResidenceDetail }
                      }
                    }
                  },
                  ROLE_DETAIL: {
                    PartyRoleType: { "@txt": "Borrower" }
                  }
                }
              },
              TAXPAYER_IDENTIFIERS: {
                "@skip": ({ borrower }) => !borrower.ssn,
                TAXPAYER_IDENTIFIER: {
                  TaxpayerIdentifierType: { "@txt": "SocialSecurityNumber" },
                  TaxpayerIdentifierValue: { "@txt": ({ borrower }) => borrower.ssn }
                }
              }
            }
          },
          RELATIONSHIPS: {
            "@skip": ({ document }) => {
              const hasFinancial = (document.applicant && document.applicant.borrowers || []).some(
                borrower => (borrower.reserves || []).some(r => parseFloat(r.funds) > 0)
              );
              const hasReo = document.investment_properties && document.investment_properties.length > 0;
              const hasLiabilities = Object.values(document.accountSummaries || {}).some(
                a => a.liability_type && parseFloat(a.monthly_payment) > 0
              );
              const hasEmployers = document.applicant.borrowers.some(
                b => b.income && b.income.sources && b.income.sources.length > 0
              );
              return !hasFinancial && !hasReo && !hasLiabilities && !hasEmployers;
            },
            "@populate": relationshipsExporter.populateRelationships
          }
        }
      }
    }
  }
};

const required = { "@required": true };
const requireOne = { "@requireOne": true };

/**
 * The MISMO XML import config.
 * This is like the export config but in reverse, providing information for how to validate and create a Lanis-format JSON document from a MISMO XML file.
 *
 * This object defines the expected structure of the XML and how to import it.
 * The root object defines the root element, which is expected to be the `ROOT_TAG_NAME` element.
 * There are different customizations available here compared to the export config:
 *
 * "@required" - Elements are optional by default, add this (mapping to `true`) to make the document fail validation if the element is missing.
 * If you add this to an element that is a descendant of an optional element, then the element is only required if the optional one is present.
 *
 * "@requireOne" - Same as required except this also requires there to be exactly one of this element, so if there are 2 or more, or 0, then validation will fail.
 * You do not need to add "@required" if this is present.
 *
 * "@skip" - This may map to a function that takes in the XML for the current element (converted to a JSON object) as the first argument and the context as the second.
 * If this function returns true, the element (and its children) will be skipped and nothing else will be done with the element.
 *
 * "@item" - This may map to a function that takes in the XML for the current element (converted to a JSON object) as the first argument and the context as the second.
 * This function is used to create a new object based on the presence of an element in the XML document, and will be invoked for each element, if there are multiple.
 * It should return an object that will be merged into the context and is available to other customizations in the element and its descendants.
 * This is also where you add the new item to the document, which is present in the context.
 * For example, `LOANS: { LOAN: { "@item": (_obj, { document }) => { create new loan, add it to document.loans, and return { loan }; } } }` will create a new loan for each LOAN element in LOANS.
 *
 * "@populate" - This may map to a function that takes in the XML for the current element (converted to a JSON object) as the first argument and the context as the second.
 * This function can do whatever it needs to import the element into the document, and is where a bulk of the custom import logic should live.
 * To continue the above example for "@item", you could place a "@populate" function anywhere in LOAN with a signature like `(obj, { loan }) => { populate data in loan based on the obj }`.
 */
export const IMPORT_CONFIG = {
  DEAL_SETS: {
    ...required,
    DEAL_SET: {
      ...requireOne,
      DEALS: {
        ...required,
        DEAL: {
          ...requireOne,
          COLLATERALS: {
            COLLATERAL: {
              "@skip": (obj) => !obj.SUBJECT_PROPERTY,
              SUBJECT_PROPERTY: {
                ADDRESS: { "@populate": propertyImporter.importPropertyAddress },
                PROPERTY_DETAIL: { "@populate": propertyImporter.importPropertyDetail },
                PROJECT: {
                  PROJECT_DETAIL: {
                    "@populate": propertyImporter.importProjectDetail
                  }
                },
                SALES_CONTRACTS: {
                  SALES_CONTRACT: {
                    SALES_CONTRACT_DETAIL: {
                      "@populate": propertyImporter.importSalesContractDetail
                    }
                  }
                },
                PROPERTY_VALUATIONS: {
                  PROPERTY_VALUATION: {
                    PROPERTY_VALUATION_DETAIL: {
                      "@populate": propertyImporter.importPropertyValuation
                    }
                  }
                }
              }
            }
          },
          LOANS: {
            LOAN: {
              "@item": (_obj, { document }) => {
                const loan = deep_clone_simple(default_loan);
                // Sales price (from COLLATERALS, imported earlier) is the offer price.
                if (
                  document.property.listed_price &&
                  document.property.listed_price !== "0"
                ) {
                  loan.offer_price = document.property.listed_price;
                }
                document.loans.push(loan);
                return { loan };
              },
              LOAN_DETAIL: { "@populate": loanImporter.importLoanDetail },
              AMORTIZATION: {
                AMORTIZATION_RULE: { "@populate": loanImporter.importAmortizationRule }
              },
              HOUSING_EXPENSES: {
                HOUSING_EXPENSE: {
                  "@populate": loanImporter.importHousingExpense
                }
              },
              REFINANCE: {
                "@populate": loanImporter.importRefinance
              },
              TERMS_OF_LOAN: { "@populate": loanImporter.importTermsOfLoan }
            }
          },
          PARTIES: {
            ...required,
            PARTY: {
              // Only real borrowers become LANIS borrowers. Real LOS/DU files
              // also carry INDIVIDUAL parties for the Loan Originator and
              // Property Owner — skipping on !INDIVIDUAL alone imported those
              // as phantom blank borrowers. Require an actual Borrower role.
              "@skip": (obj) => !obj.INDIVIDUAL || !isBorrowerParty(obj),
              "@item": (_obj, { document }) => {
                const borrower = deep_clone_simple(default_borrower);
                document.applicant.borrowers.push(borrower);
                return { borrower };
              },
              INDIVIDUAL: {
                ...required,
                NAME: { "@populate": borrowerImporter.importName },
                CONTACT_POINTS: { "@populate": borrowerImporter.importContactPoints },
                IDENTIFICATION_VERIFICATION: { "@populate": borrowerImporter.importIdentificationVerification }
              },
              ADDRESSES: { "@populate": borrowerImporter.importAddress },
              TAXPAYER_IDENTIFIERS: {
                TAXPAYER_IDENTIFIER: {
                  "@populate": borrowerImporter.importTaxpayerIdentifier
                }
              },
              ROLES: {
                ROLE: {
                  BORROWER: {
                    BORROWER_DETAIL: { "@populate": borrowerImporter.importBorrowerDetail },
                    CREDIT_SCORES: {
                      // Read all three bureau CREDIT_SCORE nodes at once so we
                      // can take the median, not last-one-wins. See
                      // importers/borrower.importCreditScores.
                      "@populate": borrowerImporter.importCreditScores
                    },
                    CURRENT_INCOME: {
                      CURRENT_INCOME_ITEMS: {
                        CURRENT_INCOME_ITEM: {
                          "@skip": (obj) => !obj.CURRENT_INCOME_ITEM_DETAIL,
                          "@item": (obj, context) => {
                            const { borrower, relationships, employerIncome } = context;
                            const detail = obj.CURRENT_INCOME_ITEM_DETAIL;

                            // Employment income belongs to an EMPLOYER record,
                            // not the "other income" list. The amount lives
                            // here, but the employer it belongs to is only
                            // knowable via the RELATIONSHIP arc. Stash it for
                            // the EMPLOYERS pass, which runs after this one.
                            if (mismoBool(detail.EmploymentIncomeIndicator)) {
                              const label = attr(obj, "xlink:label");
                              const employerLabel = relationships.incomeEmployer[label];
                              const amount = detail.CurrentIncomeMonthlyTotalAmount;
                              // Accumulate each component (Base / Overtime / Bonus /
                              // Commissions / TipIncome) for this employer; the
                              // EMPLOYERS pass maps them onto the income source.
                              // An older file with a single "Base" total still
                              // lands entirely on Base.
                              let incomeType = detail.IncomeType || "Base";
                              // A DU-certified LOS (POINT) re-expresses some
                              // employment components as IncomeType=Other with a
                              // description (Commissions is the common one - DU
                              // has no native "Commissions" enum value). Map the
                              // description back to the component name so it lands
                              // on commission/overtime/etc instead of being
                              // dropped under an unrecognized "Other" key.
                              if (
                                incomeType === "Other" &&
                                ["Commissions", "Overtime", "Bonus", "TipIncome"].includes(
                                  detail.IncomeTypeOtherDescription
                                )
                              ) {
                                incomeType = detail.IncomeTypeOtherDescription;
                              }

                              if (employerLabel && amount !== undefined) {
                                if (!employerIncome[employerLabel]) {
                                  employerIncome[employerLabel] = {};
                                }
                                employerIncome[employerLabel][incomeType] = amount;
                              }

                              // Nothing to populate on the "other income" row.
                              return { incomeOther: null };
                            }

                            const incomeOther = {
                              income_type: "",
                              description: "",
                              yearly_amount: 0
                            };
                            borrower.income.other_income.push(incomeOther);
                            return { incomeOther };
                          },
                          CURRENT_INCOME_ITEM_DETAIL: { "@populate": borrowerImporter.importIncomeItemDetail }
                        }
                      }
                    },
                    RESIDENCES: { "@populate": borrowerImporter.importResidences },
                    EMPLOYERS: {
                      EMPLOYER: {
                        "@item": (obj, { borrower, employerIncome }) => {
                          const incomeSource = deep_clone_simple(default_monthly_source);

                          // Pick up the salary that CURRENT_INCOME_ITEM stashed
                          // for this employer. LANIS exports the amount there
                          // (CurrentIncomeMonthlyTotalAmount), not on EMPLOYMENT,
                          // so without this the income imports as $0 and the DTI
                          // comes out wrong.
                          const label = attr(obj, "xlink:label");
                          const income = employerIncome[label];

                          if (income) {
                            const annual = m => payment_to_year(m, 12);
                            if (income.Base !== undefined) {
                              incomeSource.base_income_0 = annual(income.Base);
                            }
                            // Variables were exported as the 2-year average; set
                            // BOTH years so income_source_yearly_amount (which
                            // averages _0/_1) reproduces the same figure.
                            if (income.Overtime !== undefined) {
                              incomeSource.overtime_0 = annual(income.Overtime);
                              incomeSource.overtime_1 = annual(income.Overtime);
                            }
                            if (income.Bonus !== undefined) {
                              incomeSource.bonuses_0 = annual(income.Bonus);
                              incomeSource.bonuses_1 = annual(income.Bonus);
                            }
                            if (income.Commissions !== undefined) {
                              incomeSource.commission_0 = annual(income.Commissions);
                              incomeSource.commission_1 = annual(income.Commissions);
                            }
                            if (income.TipIncome !== undefined) {
                              incomeSource.tips_0 = annual(income.TipIncome);
                              incomeSource.tips_1 = annual(income.TipIncome);
                            }
                          }

                          borrower.income.sources.push(incomeSource);
                          return { incomeSource };
                        },
                        LEGAL_ENTITY: {
                          CONTACTS: { "@populate": borrowerImporter.importEmployerContacts },
                          LEGAL_ENTITY_DETAIL: { "@populate": borrowerImporter.importEmployerName }
                        },
                        EMPLOYMENT: { "@populate": borrowerImporter.importEmployerEmployment },
                        ADDRESS: { "@populate": borrowerImporter.importEmployerAddress }
                      }
                    },
                    GOVERNMENT_BORROWER: {
                      "@populate": (obj, { borrower }) => {
                        if (mismoBool(obj.VeteranStatusIndicator)) {
                          borrower.is_veteran = YES;
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          // The ASSETS block carries two different kinds of record: a bank
          // account (ASSET_DETAIL) and a Real Estate Owned property
          // (OWNED_PROPERTY). One ASSET node is only ever one of the two, so a
          // single pass handles both and each populator guards on its own
          // context being present.
          ASSETS: {
            ASSET: {
              "@skip": obj => !obj.ASSET_DETAIL && !obj.OWNED_PROPERTY,
              "@item": (obj, { document, relationships }) => {
                // Real Estate Owned — no reserve record for this one. Pass the
                // owner indices (from the ASSET's arcs) down to the REO record.
                if (!obj.ASSET_DETAIL) {
                  const reoLabel = attr(obj, "xlink:label");
                  const reoOwners = (relationships.assetOwners[reoLabel] || [])
                    .filter(i => document.applicant.borrowers[i])
                    .map(String);
                  return { reserve: null, reoOwners, reoAssetLabel: reoLabel };
                }

                const reserve = deep_clone_simple(default_reserve);

                // Who owns this account? MISMO records that as a RELATIONSHIP
                // arc, not on the ASSET itself. A reserve holds a single owner,
                // so for a jointly-arced asset we take the primary (first)
                // borrower. Fall back to Borrower 1 when there is no arc.
                const label = attr(obj, "xlink:label");
                const owners = relationships.assetOwners[label] || [];
                const borrowers = document.applicant.borrowers;
                const owner = owners.length > 0 ? owners[0] : undefined;
                const index =
                  owner !== undefined && borrowers[owner] ? owner : 0;

                reserve.borrower = String(index);

                if (borrowers[index]) {
                  borrowers[index].reserves.push(reserve);
                }
                return { reserve };
              },
              ASSET_DETAIL: { "@populate": assetImporter.importAssetDetail },
              ASSET_HOLDER: { "@populate": assetImporter.importAssetHolder },
              OWNED_PROPERTY: {
                "@item": reoImporter.createReoRecord,
                OWNED_PROPERTY_DETAIL: {
                  "@populate": reoImporter.importOwnedPropertyDetail
                },
                PROPERTY: {
                  ADDRESS: { "@populate": reoImporter.importReoAddress },
                  PROPERTY_DETAIL: {
                    "@populate": reoImporter.importReoPropertyDetail
                  }
                }
              }
            }
          },
          // Debts. Ownership comes from the RELATIONSHIPS block; the LIABILITY
          // element itself says nothing about whose debt it is.
          LIABILITIES: {
            LIABILITY: {
              "@skip": obj => !obj.LIABILITY_DETAIL,
              "@item": (obj, context) => {
                const label = attr(obj, "xlink:label");
                const borrowers = context.document.applicant.borrowers;

                // Owners = every borrower arced to this liability. Joint debts
                // have two+ arcs; a POINT file writes the same debt as separate
                // per-borrower elements. Both are collapsed to one row here by
                // deduping on account # + creditor and unioning the owners, so
                // a joint account shows once (not twice).
                const arcOwners = (context.relationships.liabilityOwners[label] || [])
                  .filter(i => borrowers[i]);

                return liabilityImporter.upsertLiabilityRecord(
                  obj,
                  context,
                  arcOwners
                );
              },
              LIABILITY_DETAIL: { "@populate": liabilityImporter.importLiability },
              LIABILITY_HOLDER: {
                "@populate": liabilityImporter.importLiabilityHolder
              }
            }
          }
        }
      }
    }
  }
};
